import React from 'react';
import { Star, MapPin, ExternalLink, Trophy, Dumbbell, ChevronRight } from 'lucide-react';

// ── Monthly Spotlight Config ──
// Update this object each month to rotate the featured gym
const SPOTLIGHT = {
  month: 'April 2026',
  gym: {
    name: 'PITFIT',
    city: 'Dallas',
    state: 'TX',
    tier: 'HYROX Training Club',
    rating: '5.0',
    website: 'https://pitfitworkout.com/',
    zip: '75205',
  },
  tagline: "Dallas's Hottest HYROX Training — Now with Dedicated Race Prep Classes",
  whyTrainHere: "PITFIT isn't your standard gym — it's an open-air, high-intensity training facility in a parking garage that's become a cult favorite for Dallas athletes. Now offering dedicated HYROX Total Body classes led by Coach Sara Yingling (@yingers1), a competitive HYROX athlete and CrossFit Games veteran, and Coach Evan Conrad, a HYROX race prep specialist. Use discount code 'Ninja' at checkout for a special offer.",
  highlights: [
    { label: 'HYROX Total Body Classes', icon: 'trophy' },
    { label: 'Coach Sara Yingling & Evan Conrad', icon: 'users' },
    { label: '5.0 Rating (15,000+ reviews)', icon: 'medal' },
    { label: "Use Code 'Ninja' at Checkout", icon: 'dumbbell' },
  ],
  reviews: [
    { text: "Loved how the instructor had so much energy to cheer on everyone during the HYROX class. It was really needed for a 6am class.", author: 'ClassPass Member', detail: 'HYROX Total Body' },
    { text: "Great workout and friendly staff with motivated trainers! Definitely will be back.", author: 'Recent Review', detail: 'HYROX class attendee' },
    { text: "That's why I call Chris the calorie killer! The PIT is no joke — best workout in Dallas.", author: 'PitFit Regular', detail: 'Unlimited member' },
  ],
};

const ICON_MAP = {
  trophy: Trophy,
  users: ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
  ),
  medal: ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M7.21 15 2.66 7.14a2 2 0 0 1 .13-2.2L4.4 2.8A2 2 0 0 1 6 2h12a2 2 0 0 1 1.6.8l1.6 2.14a2 2 0 0 1 .14 2.2L16.79 15"/><path d="M11 12 5.12 2.2"/><path d="m13 12 5.88-9.8"/><path d="M8 7h8"/><circle cx="12" cy="17" r="5"/><path d="M12 18v-2h-.5"/></svg>
  ),
  dumbbell: Dumbbell,
};

const GymOfTheMonth = ({ onViewOnMap }) => {
  const { month, gym, tagline, whyTrainHere, highlights, reviews } = SPOTLIGHT;

  return (
    <div className="mb-8" data-testid="gym-of-the-month">
      {/* Top accent bar */}
      <div className="relative bg-gradient-to-r from-[#0a0a0f] via-[#1a1a0a] to-[#0a0a0f] border border-[#e8ff00]/30 overflow-hidden">
        {/* Gold top stripe */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#e8ff00] to-transparent" />

        <div className="p-5 sm:p-7">
          {/* Badge row */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 bg-[#e8ff00] px-3 py-1.5">
                <Trophy className="w-3.5 h-3.5 text-black" />
                <span className="text-black text-[11px] font-heading uppercase tracking-wider">Gym of the Month</span>
              </div>
              <span className="text-zinc-600 text-xs font-heading">{month}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 px-2 py-1 bg-zinc-800 border border-zinc-700">
                <Star className="w-3 h-3 text-[#e8ff00]" />
                <span className="text-white text-xs font-heading">{gym.rating}</span>
              </div>
              <span className="text-[#e8ff00] text-[10px] font-heading uppercase tracking-wider px-2 py-1 border border-[#e8ff00]/30 bg-[#e8ff00]/5">{gym.tier}</span>
            </div>
          </div>

          {/* Gym name & location */}
          <h3 className="text-white font-heading text-2xl sm:text-3xl mb-1" data-testid="gotm-gym-name">{gym.name}</h3>
          <p className="text-zinc-500 text-sm flex items-center gap-1.5 mb-4">
            <MapPin className="w-3.5 h-3.5" /> {gym.city}, {gym.state} {gym.zip}
          </p>

          {/* Tagline */}
          <p className="text-[#e8ff00] text-sm font-heading tracking-wide mb-5">{tagline}</p>

          {/* Why Train Here */}
          <div className="mb-6">
            <p className="text-zinc-400 text-sm leading-relaxed">{whyTrainHere}</p>
          </div>

          {/* Highlights grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="gotm-highlights">
            {highlights.map((h, i) => {
              const IconComp = ICON_MAP[h.icon] || Dumbbell;
              return (
                <div key={h.label} className="flex items-center gap-2.5 bg-zinc-900/80 border border-zinc-800 px-3 py-2.5">
                  <IconComp className="w-4 h-4 text-[#e8ff00] flex-shrink-0" />
                  <span className="text-zinc-300 text-xs font-heading">{h.label}</span>
                </div>
              );
            })}
          </div>

          {/* Reviews */}
          <div className="space-y-3 mb-6" data-testid="gotm-reviews">
            {reviews.map((r, i) => (
              <div key={r.text} className="bg-zinc-900/50 border border-zinc-800/50 px-4 py-3">
                <p className="text-zinc-300 text-sm italic leading-relaxed">"{r.text}"</p>
                <p className="text-zinc-600 text-xs mt-1.5">
                  <span className="text-zinc-400 font-heading">{r.author}</span> · {r.detail}
                </p>
              </div>
            ))}
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={gym.website}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-[#e8ff00] text-black px-6 py-3 font-heading text-sm uppercase tracking-wider hover:opacity-90 transition-opacity"
              data-testid="gotm-visit-btn"
            >
              Visit Gym <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <button
              onClick={() => onViewOnMap && onViewOnMap(gym.state)}
              className="flex items-center justify-center gap-2 bg-transparent border border-zinc-700 text-zinc-300 px-6 py-3 font-heading text-sm uppercase tracking-wider hover:border-[#e8ff00] hover:text-white transition-all"
              data-testid="gotm-view-map-btn"
            >
              View on Map <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GymOfTheMonth;
